import { captureException } from '@sentry/react'
import { type UseMutationOptions, useMutation } from '@tanstack/react-query'
import { z } from 'zod'
import { PhotoPresignedData } from '@/features/photoset/types'
import { errorHandler, parseAxiosPromise } from '@/helpers/utils'
import { MUTATION_KEYS } from '@/query-client'
import {
	type HttpBulkUploadPhotosOptions,
	httpBulkUploadPhotos
} from '../http/photoset.http'

export interface UseBulkUploadPhotos {
	photosetId: number
	options?: UseMutationOptions<
		PhotoPresignedData[],
		unknown,
		HttpBulkUploadPhotosOptions['payload']
	>
}

export const useBulkUploadPhotos = ({
	photosetId,
	options
}: UseBulkUploadPhotos) => {
	return useMutation({
		mutationKey: [MUTATION_KEYS.BULK_UPLOAD_PHOTOS],
		mutationFn: (payload: HttpBulkUploadPhotosOptions['payload']) =>
			parseAxiosPromise({
				axiosPromise: httpBulkUploadPhotos({
					urlParams: { photosetId },
					payload
				}),
				responseShape: z.array(PhotoPresignedData),
				onZodError: captureException
			}),
		...options,
		onError: (error, variables, onMutateResult, context) => {
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onSuccess: (data, variables, onMutateResult, context) => {
			options?.onSuccess?.(data, variables, onMutateResult, context)
		}
	})
}
